import { useMemo } from 'react';
import { motion } from 'framer-motion';
import { useAppState } from '../hooks/useAppState';
import { getScenesByCategory, getSceneById, getScenesByIdentity } from '../data/scenes';
import { getIdentityById } from '../data/identities';
import { CategoryFilter } from '../components/CategoryFilter';
import type { Category, IdentityId } from '../types';

const categoryLabel: Record<Category, string> = {
  automation: '自动化',
  management: '管理',
  content: '内容',
  acquisition: '获客',
};

export function AllScenesPage(): JSX.Element {
  const { state, selectScene, selectIdentity, setSceneIndex, navigate, setCategory } = useAppState();

  const filteredScenes = useMemo(
    () => getScenesByCategory(state.selectedCategory),
    [state.selectedCategory]
  );

  const handleCardClick = (sceneId: string) => {
    const scene = getSceneById(sceneId);
    if (scene && scene.identityIds.length > 0) {
      const identityId = scene.identityIds[0] as IdentityId;
      const identityScenes = getScenesByIdentity(identityId);
      const sceneIdx = identityScenes.findIndex((s) => s.id === sceneId);

      selectIdentity(identityId);
      if (sceneIdx >= 0) {
        setSceneIndex(sceneIdx);
      }
      selectScene(sceneId);
      navigate('sceneFlow');
    }
  };

  return (
    <div
      style={{
        padding: '20px',
        height: '100%',
        overflowY: 'auto',
        boxSizing: 'border-box',
        backgroundColor: '#f5f5f7',
        paddingBottom: 'calc(20px + 52px + env(safe-area-inset-bottom))',
      }}
    >
      <div style={{ marginBottom: '16px' }}>
        <h1
          style={{
            fontSize: '18px',
            fontWeight: 600,
            color: '#1a1a1c',
            margin: '0 0 4px 0',
            letterSpacing: '0.02em',
          }}
        >
          全部玩法
        </h1>
        <p
          style={{
            fontSize: '13px',
            color: '#a1a1a6',
            margin: 0,
            letterSpacing: '0.02em',
          }}
        >
          共 {filteredScenes.length} 个场景
        </p>
      </div>

      <CategoryFilter selected={state.selectedCategory} onSelect={setCategory} />

      <div
        style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(2, 1fr)',
          gap: '10px',
          marginTop: '16px',
        }}
      >
        {filteredScenes.map((scene, index) => {
          const identity = getIdentityById(scene.identityIds[0]);
          return (
            <motion.div
              key={scene.id}
              initial={{ opacity: 0, y: 12 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: index * 0.03, duration: 0.25, ease: [0.22, 1, 0.36, 1] }}
              whileTap={{ scale: 0.97 }}
              onClick={() => handleCardClick(scene.id)}
              style={{
                padding: '18px',
                borderRadius: '14px',
                backgroundColor: '#ffffff',
                border: '1px solid rgba(0, 0, 0, 0.05)',
                cursor: 'pointer',
                display: 'flex',
                flexDirection: 'column',
                gap: '10px',
                boxShadow: '0 2px 8px rgba(0,0,0,0.03)',
                transition: 'border-color 0.3s cubic-bezier(0.22, 1, 0.36, 1)',
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.borderColor = 'rgba(0, 0, 0, 0.1)';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.borderColor = 'rgba(0, 0, 0, 0.05)';
              }}
            >
              <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                <span
                  style={{
                    fontSize: '10px',
                    fontWeight: 600,
                    color: '#a1a1a6',
                    textTransform: 'uppercase',
                    letterSpacing: '0.1em',
                  }}
                >
                  {categoryLabel[scene.category]}
                </span>
                {identity && (
                  <span
                    style={{
                      fontSize: '10px',
                      fontWeight: 500,
                      color: '#c7c7cc',
                      letterSpacing: '0.05em',
                    }}
                  >
                    · {identity.name}
                  </span>
                )}
              </div>
              <span
                style={{
                  fontSize: '14px',
                  fontWeight: 600,
                  color: '#1a1a1c',
                  lineHeight: 1.3,
                  letterSpacing: '0.01em',
                }}
              >
                {scene.title}
              </span>
              <span
                style={{
                  fontSize: '12px',
                  color: '#86868b',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  display: '-webkit-box',
                  WebkitLineClamp: 2,
                  WebkitBoxOrient: 'vertical',
                  lineHeight: 1.4,
                }}
              >
                {scene.description}
              </span>
            </motion.div>
          );
        })}
      </div>
    </div>
  );
}
